import { useCallback } from 'react'
import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { useSWRConfig } from 'swr'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import { useCoursePathKey } from '../hooks/use-course-data'
import type { CourseDataInterface } from '../types/course-interface'
import type { CourseLectureTrackingSessionCreateInterface } from '../types/course-lecture-interface'
import { TrackingApi } from './session-logging-api'

export const useCreateLectureTrackingSession = (): UseMutationResult<
  CourseLectureTrackingSessionCreateInterface,
  Error,
  number
> => {
  const { mutate } = useSWRConfig()
  const courseDataKey = useCoursePathKey()
  const accessDeniedResponseHandler = useCallback(() => null, [])

  const lockedResponseHandler = useCallback(
    async () =>
      mutate(
        courseDataKey,
        (data?: CourseDataInterface) => (data ? { ...data, lectureSessionLogging: false } : data),
        { revalidate: true },
      ),
    [courseDataKey, mutate],
  )

  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.post,
    CourseLectureTrackingSessionCreateInterface
  >({
    method: RequestMethodsEnum.post,
    accessDeniedResponseHandler,
    lockedResponseHandler,
  })

  return useMutation({
    mutationFn: async (lectureId: number) =>
      fetcher(`${TrackingApi.InitLecture}/${lectureId}/session/create`),
    retry: false,
  })
}
